import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

export const useUnreadWritingCount = () => {
  const { user } = useAuth();
  const { isCoach } = useUserRole();
  const [unreadCount, setUnreadCount] = useState(0);
  const [submitters, setSubmitters] = useState<string[]>([]);

  const fetchCount = useCallback(async () => {
    if (!user || !isCoach) return;
    const { data, error } = await (supabase
      .from('writing_exercises' as any)
      .select('id, client_id')
      .eq('status', 'submitted') as any);

    if (!error) {
      setUnreadCount(data?.length || 0);
      const clientIds = Array.from(new Set((data || []).map((r: any) => r.client_id).filter(Boolean)));
      if (clientIds.length === 0) {
        setSubmitters([]);
        return;
      }
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, full_name')
        .in('id', clientIds as string[]);
      const nameById = new Map<string, string>();
      (profiles || []).forEach((p: any) => {
        const n = (p.first_name || (p.full_name || '').split(' ')[0] || 'Client').trim();
        nameById.set(p.id, n);
      });
      const names = (data || []).map((r: any) => nameById.get(r.client_id) || 'Client');
      setSubmitters(names);
    }
  }, [user, isCoach]);

  useEffect(() => {
    if (!user || !isCoach) return;

    fetchCount();

    const channel = supabase
      .channel(`writing_exercises_unread_${user.id}_${Math.random().toString(36).slice(2, 10)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'writing_exercises',
        },
        () => fetchCount()
      )
      .subscribe();

    // Also listen for visibility changes to refetch when tab becomes active
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, isCoach, fetchCount]);

  return { unreadCount, submitters } as const;
};

// Backwards-compatible alias if any caller expects a plain number
export default useUnreadWritingCount;
